<template>
	<template v-if="visible">
		<template v-if="display.mode === 'form'">
			<div class="form fd-form fd-root" :class="[{ 'fd-data-changed': wasChange }, clazz]" :style="style" v-bind="$attrs">
				<div v-if="hasHead" class="form-head">
					<div v-if="hasTitle" class="form-head-left">
						<component v-if="fd.header" :is="componentOfPart(fd.header)" :part="partRef(fd.header)" :data="data" :readonly="ro" :viewmode="vm"></component>
						<div v-else class="fd-flow">
							<sym-icon v-if="fd.headerIcon" :name="fd.headerIcon"></sym-icon>
							<span v-if="fd.headerTitle">{{ fd.headerTitle }}</span>
						</div>
					</div>
					<div v-if="hasSecondaryControls" class="form-head-right"></div>
				</div>
				<div class="form-error-body e2e is-container" v-if="errors.length > 0">
					<api-error v-for="err in errors" tag="div" :level="err.level" :message="err.message" :html="!!err.html" :icon="err.icon" :actions="err.actions" class="no-border e2e-padding"></api-error>
				</div>
				<div class="form-body" ref="formBody">
					<component :is="componentOfPart(fd.body)" :part="partRef(fd.body)" :data="data" :readonly="ro" :viewmode="vm"></component>
				</div>
				<div v-if="hasFoot" class="form-foot">
					<template v-if="!!fd.status && !!fd.controls">
						<div class="form-foot-left">
							<component :is="componentOfPart(fd.status)" :part="partRef(fd.status)" :data="data" :readonly="ro" :viewmode="vm"></component>
						</div>
						<div class="form-foot-center">
							<component :is="componentOfPart(fd.controls)" :part="partRef(fd.controls)" :data="data" :readonly="ro" :viewmode="vm"></component>
						</div>
					</template>
					<template v-else>
						<div v-if="!!fd.status" class="form-foot-left">
							<component :is="componentOfPart(fd.status)" :part="partRef(fd.status)" :data="data" :readonly="ro" :viewmode="vm"></component>
						</div>
						<div v-if="!!fd.controls" class="form-foot-left">
							<component :is="componentOfPart(fd.controls)" :part="partRef(fd.controls)" :data="data" :readonly="ro" :viewmode="vm"></component>
						</div>
					</template>
					<div v-if="actions" class="form-foot-right">
						<component :is="componentOfPart(actions)" :part="partRef(actions)" :data="data" :readonly="ro" :viewmode="vm"></component>
					</div>
				</div>
			</div>
		</template>
		<template v-else-if="display.mode === 'body'"><component :is="componentOfPart(fd.body)" :part="partRef(fd.body)" :data="data" :readonly="ro" :viewmode="vm"></component></template>
		<template v-else-if="display.mode === 'window' || display.mode === 'modal'">
			<dialog-window :icon="fd.headerIcon" :title="fd.headerTitle" :moveable="fd.moveable ?? true" :resizable="fd.resizable ?? true"
						   :width="fd.initialWidth ?? 0" :show="display.mode" class="fd-root form" @close="dialogClosed" :global-errors="errors.length > 0" ref="dialogWindow">
				<template v-if="hasTitle" #header>
					<component v-if="fd.header" :is="componentOfPart(fd.header)" :part="partRef(fd.header)" :data="data" :readonly="ro" :viewmode="vm"></component>
					<div v-else class="fd-flow">
						<sym-icon v-if="fd.headerIcon" :name="fd.headerIcon"></sym-icon>
						<span v-if="fd.headerTitle">{{ fd.headerTitle }}</span>
					</div>
				</template>
				<template #default><component :is="componentOfPart(fd.body)" :part="partRef(fd.body)" :data="data" :readonly="ro" :viewmode="vm"></component></template>
				<template v-if="!!fd.status" #status><component :is="componentOfPart(fd.status)" :part="partRef(fd.status)" :data="data" :readonly="ro" :viewmode="vm"></component></template>
				<template v-if="!!fd.controls" #secondary_controls><component :is="componentOfPart(fd.controls)" :part="partRef(fd.controls)" :data="data" :readonly="ro" :viewmode="vm"></component></template>
				<template v-if="actions" #controls><component :is="componentOfPart(actions)" :part="partRef(actions)" :data="data" :readonly="ro" :viewmode="vm"></component></template>
				<template #errors><api-error v-for="err in errors" tag="div" :level="err.level" :message="err.message" :html="!!err.html" :icon="err.icon" :actions="err.actions" class="no-border e2e-padding"></api-error></template>
			</dialog-window>
		</template>
		<template v-else><div class="err">Invalid form display mode {{ display.mode }}</div></template>
	</template>
</template>

<script setup lang="ts">
import useContainer, { type ContainerProps } from "./Container";
import {computed, ref, watch} from "vue";
import {type ErrorMap} from "../PartInjectKeys";
import {injectEnv, injectEventBus} from "../Environment";
import type {FormDefinition} from "@t/fd/layout/FormDefinition";
import type {FormPart} from "@t/fd/FormPart";
import type {DialogDisplay, DialogMode} from "@t/ui/Dialog.ts";
import ApiError from "@fd/helpers/ApiError.vue";
import SymIcon from "@fd/helpers/SymIcon.vue";
import DialogWindow from "@/dialog/DialogWindow.vue";

interface FormPartProps extends ContainerProps {
	actions?: FormPart,
	display?: DialogDisplay,
	errs?: ErrorMap
}
const props = withDefaults(defineProps<FormPartProps>(), {
	display: () => <DialogDisplay>{ mode: "form" },
	errs: () => []
});

const emit = defineEmits(["close"]);

//let fd: FormDefinition = props.part as FormDefinition;
const fd = computed<FormDefinition>(() => props.part as FormDefinition);
const env = injectEnv();

const {componentOfPart, partRef, visible, vm, localData, ro, errors, style, clazz} = useContainer(props as FormPartProps);

const hasTitle = computed<boolean>(() => {
	return !!fd.value.header || !!fd.value.headerIcon || !!fd.value.headerTitle;
});
const hasSecondaryControls = computed<boolean>(() => false);
const hasHead = computed<boolean>(() => hasTitle.value || hasSecondaryControls.value);
const hasFoot = computed<boolean>(() => !!fd.value.status || !!fd.value.controls || !!props.actions);

const formBody = ref<HTMLDivElement>(null);

function dialogClosed() {
	emit("close");
}

// Detect data change
const wasChange = ref<boolean>(false);
watch(() => localData.value, (n, o)=>{
    wasChange.value = true;
}, {
    deep: true
});
function setDirty(dirtyFlag:boolean):void {
    wasChange.value = dirtyFlag;
}

const dialogWindow = ref(null);
function closeDialog() {
	if (dialogWindow.value) {
		dialogWindow.value.close();
	}
}

watch(formBody, (newBody, oldBody) => {
	if (!oldBody && newBody) {
		// Direct form just appeared
		const af = newBody.querySelector(".autofocus") as HTMLInputElement;
		af?.focus();
	}
});

defineExpose({
	setDirty,
	/** Animated close of dialog window */
	closeDialog
});
</script>

<style>
.fd-root {
	display: flex;
	flex-direction: column;
	align-items: stretch;	/* To take available width */
	justify-content: flex-start;
	padding: 0;
	margin: 0;
	gap: 0;
}


@media not all and (hover: none) and (pointer: coarse) {
	/* Non-touch devices */
	/* Form is made smaller only on non-touch devices */
	.fd-small {
		--font-size-s: 11px;
		--font-size: 12px;
		--font-size-l: 14px;
		font-size: var(--font-size);

		--input-spacing: 3px;
		--gap: 6px;

		--input-font-size: 12px;
		--label-font-size: 12px;
		--checkbox-label-font-size: 12px;
	}
}

.fd-thin {
	font-weight: 300;
}

/* Smaller text used as help (explanatory) messages or less important descriptions */
.fd-help {
	font-size: var(--font-size-s);
	/* opacity: 0.6; */
}

/* Bigger text labels used to separate parts of the form */
.fd-title {
	font-size: var(--font-size-l);
	font-weight: bold;
}

.warn {
    background-color: var(--warn-bg);
    color: var(--warn-bg);
}
</style>
